import React, { useEffect, useRef, useState } from 'react';
import { CalendarIcon } from '@navi/web-ui/lib/icons';
import { Typography } from '@navi/web-ui/lib/primitives';
import { DATE_TIME_TYPE } from './constants';
import useOutsideClick from '@hooks/useOutsideClick';
import ClockIcon from '@icons/ClockIcon';
import DateTimePickerLabel from '@cp/components/DateTimePicker/DateTimePickerLabel';
import styles from './index.module.scss';
import cx from 'classnames';
import FooterContainer from './Footer';
import HeadingContainer from './Heading';

interface DatePickerProps extends React.InputHTMLAttributes<HTMLInputElement> {
  heading?: string;
  type: string;
  value: string;
  className?: string;
  containerClasses?: string;
  inputClasses?: string;
  iconColor?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  footer?: string;
  updateDateLabel?: boolean;
  customLabelColor?: string;
}

const DateTimePickerComponent: React.FC<DatePickerProps> = ({
  heading,
  type,
  value,
  id,
  className,
  containerClasses,
  inputClasses,
  iconColor,
  onChange,
  disabled = false,
  footer,
  updateDateLabel = false,
  customLabelColor,
  ...restProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [date, setDate] = React.useState<string | Date>(value);
  const datePickerRef = useRef(null);
  const datePickerContainerRef = useRef(null);

  const handleDateTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value;
    setDate(selectedValue);
    setIsOpen(false);
    if (onChange && typeof onChange === 'function') {
      onChange(event);
    }
  };

  const handleShowPicker = () => {
    if (disabled) {
      return;
    }
    setIsOpen(isOpen => !isOpen);
  };

  useOutsideClick(datePickerContainerRef, () => {
    setIsOpen(false);
  });

  useEffect(() => {
    if (isOpen && !disabled) {
      datePickerRef?.current?.showPicker?.();
    }
  }, [isOpen]);

  useEffect(() => {
    if (
      [DATE_TIME_TYPE.DATE_TIME, DATE_TIME_TYPE.TIME].includes(type as DATE_TIME_TYPE) &&
      !date &&
      value
    ) {
      setDate(value);
      return;
    }

    if (updateDateLabel) {
      setDate(value);
    }
  }, [value]);

  return (
    <div
      className={cx(styles.datePickerInput, className, { [styles.disabled]: disabled })}
      ref={datePickerContainerRef}
    >
      {heading ? <HeadingContainer heading={heading} /> : null}

      <div
        className={cx(styles.datePickerContainer, containerClasses, {
          [styles.disabled]: disabled
        })}
      >
        <input
          type={type}
          required={true}
          id={id}
          value={value}
          ref={datePickerRef}
          onChange={handleDateTimeChange}
          disabled={disabled}
          {...restProps}
        />
        <div
          className={cx(styles.datePickerLabel, inputClasses, customLabelColor, {
            [styles.disabled]: disabled
          })}
          onClick={handleShowPicker}
        >
          <DateTimePickerLabel date={date as string} type={type as DATE_TIME_TYPE} id={id} />
          {type === DATE_TIME_TYPE.TIME ? (
            <ClockIcon
              width={20}
              height={20}
              color={
                disabled ? 'var(--grayscale-content-3)' : iconColor || 'var(--grayscale-content-3)'
              }
            />
          ) : (
            <CalendarIcon
              width={20}
              height={20}
              color={
                disabled ? 'var(--grayscale-content-3)' : iconColor || 'var(--grayscale-content-3)'
              }
            />
          )}
        </div>
      </div>
      {footer ? <FooterContainer footer={footer} /> : null}
    </div>
  );
};
export default DateTimePickerComponent;
